'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

const DEFAULT_SLIDES = ['/assets/images/tattoo_bg01-1778481619698.jpg'];

export default function HeroSection() {
  const [slides, setSlides] = useState<string[]>(DEFAULT_SLIDES);
  const [activeIndex, setActiveIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  // Fetch hero images from Supabase app_settings
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('app_settings')
          .select('key, value')
          .in('key', ['hero_slide_1', 'hero_slide_2', 'hero_slide_3']);
        if (data && data.length > 0) {
          const map: Record<string, string> = {};
          data.forEach((row) => { if (row.value) map[row.key] = row.value; });
          const fetched = [
            map['hero_slide_1'] || DEFAULT_SLIDES[0],
            map['hero_slide_2'],
            map['hero_slide_3'],
          ].filter(Boolean) as string[];
          if (fetched.length > 0) setSlides(fetched);
        }
      } catch {
        // Use defaults
      }
    };
    fetchSlides();
  }, []);

  // Slideshow with dissolve every 12 seconds
  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      const next = (activeIndex + 1) % slides.length;
      setNextIndex(next);
      // Small delay to ensure next slide is rendered before starting fade-in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitioning(true);
        });
      });
      setTimeout(() => {
        setActiveIndex(next);
        setNextIndex(null);
        setTransitioning(false);
      }, 2200);
    }, 12000);
    return () => clearInterval(interval);
  }, [slides, activeIndex]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background slideshow */}
      <div className="absolute inset-0">
        {/* Current (base) slide — always fully visible */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${slides[activeIndex]})`,
            opacity: 1,
          }}
        />
        {/* Next slide fades in on top */}
        {nextIndex !== null && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${slides[nextIndex]})`,
              opacity: transitioning ? 1 : 0,
              transition: transitioning ? 'opacity 2000ms ease-in-out' : 'none',
            }}
          />
        )}
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-background/70" />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-semibold tracking-wide mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
          Now in Early Access — Free to Try
        </div>

        {/* Headline */}
        <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold leading-none tracking-tight mb-6">
          <span className="text-foreground">Try Before</span>
          <br />
          <span className="text-gradient">You Ink.</span>
        </h1>

        <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
          Upload your photo privately, place any tattoo design on your skin, and see exactly how it
          looks — before you ever sit in the chair. Discover local artists. Save your favorites.
          Your previews stay yours alone.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Link href="/tattoo-preview-editor" className="btn-primary text-base px-7 py-3.5 ink-glow">
            <Icon name="SparklesIcon" size={18} variant="solid" />
            Try It Now — Free
          </Link>
          <Link href="/user-dashboard" className="btn-secondary text-base px-7 py-3.5">
            <Icon name="MagnifyingGlassIcon" size={18} variant="outline" />
            Find Artists
          </Link>
          <Link href="/sign-up-login-screen" className="btn-ghost text-base px-7 py-3.5 border border-border">
            Artist Sign Up
          </Link>
        </div>

        {/* Social proof strip */}
        <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-muted-foreground">
          {[
            { value: '12,400+', label: 'Tattoos Tried' },
            { value: '840+', label: 'Artists Listed' },
            { value: '3,200+', label: 'Users Registered' },
            { value: '100%', label: 'Private & Encrypted' },
          ]?.map((stat) => (
            <div key={`hero-stat-${stat?.label?.toLowerCase()?.replace(/\s/g, '-')}`} className="text-center">
              <div className="text-2xl font-bold text-primary tabular-nums">{stat?.value}</div>
              <div className="text-xs mt-0.5">{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Slide indicators */}
        {slides.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            {slides.map((_, i) => (
              <button
                key={`slide-dot-${i}`}
                onClick={() => {
                  if (i !== activeIndex && !transitioning) {
                    setNextIndex(i);
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        setTransitioning(true);
                      });
                    });
                    setTimeout(() => {
                      setActiveIndex(i);
                      setNextIndex(null);
                      setTransitioning(false);
                    }, 2200);
                  }
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === activeIndex ? 'bg-primary w-6' : 'bg-primary/30 hover:bg-primary/60'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}